import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Linkedin } from "lucide-react";
import { site, en } from "@/content";
import { useLang } from "@/lang";
import MacWindow from "@/components/MacWindow";
import { EASE, MaskedLine } from "@/components/Reveal";

export default function Hero() {
    const { lang } = useLang();
    const s = lang === "en" ? en.site : site;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const watermarkY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <div id="top" ref={ref} data-testid="hero-section" className="scroll-mt-16">
            <MacWindow title={s.windowWelcome} icon="mac">
                <div className="relative flex min-h-[70vh] flex-col justify-between overflow-hidden">
                    <motion.span
                        aria-hidden="true"
                        style={{ y: watermarkY }}
                        className="text-outline pointer-events-none absolute -right-2 -top-6 select-none font-display text-[20vw] leading-none"
                    >
                        {s.lastName}
                    </motion.span>

                    <motion.div style={{ opacity: fade }} className="relative">
                        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.25em] text-ink/70">
                            <MaskedLine immediate delay={0.1} key={lang}>
                                {s.role}
                            </MaskedLine>
                            <MaskedLine immediate delay={0.2} className="hidden md:block">
                                {s.domain}
                            </MaskedLine>
                        </div>

                        <h1
                            data-testid="hero-name"
                            className="mt-8 font-display text-[clamp(3rem,10vw,8.5rem)] leading-[0.92] tracking-tight md:mt-12"
                        >
                            <MaskedLine immediate delay={0.35}>
                                {s.firstName}
                            </MaskedLine>
                            <MaskedLine immediate delay={0.5} className="md:pl-[12vw]">
                                <em>{s.lastName}</em>
                                <span className="blink ml-3 inline-block h-[0.68em] w-[0.32em] bg-[#6CC9C8] align-baseline" />
                            </MaskedLine>
                        </h1>
                    </motion.div>

                    <motion.div
                        style={{ opacity: fade }}
                        className="relative grid gap-8 pt-14 md:grid-cols-12 md:items-end"
                    >
                        <div className="md:col-span-6">
                            <MaskedLine immediate delay={0.75} key={`tag-${lang}`}>
                                <span
                                    data-testid="hero-tagline"
                                    className="font-accent-serif text-3xl leading-snug text-ink/90 md:text-5xl"
                                >
                                    “{s.tagline}”
                                </span>
                            </MaskedLine>
                        </div>
                        <div className="md:col-span-5 md:col-start-8">
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
                            >
                                <p className="max-w-md text-base leading-relaxed text-ink/70 md:text-lg">
                                    {s.intro}
                                </p>
                                <div className="mt-6 flex flex-wrap items-center gap-4">
                                    <a
                                        href={s.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="linkedin-cta-button"
                                        className="group flex items-center gap-3 border-2 border-ink bg-ink px-7 py-3.5 text-xs font-extrabold uppercase tracking-[0.12em] text-paper transition-colors duration-150 hover:bg-paper hover:text-ink"
                                    >
                                        <Linkedin className="h-4 w-4" />
                                        {s.ctaLinkedin}
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.6, duration: 0.8 }}
                        className="mt-10 hidden items-center justify-center gap-3 md:flex"
                        data-testid="scroll-hint"
                    >
                        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-ink/60">
                            {s.scrollLabel}
                        </span>
                        <span className="scroll-hint-line block h-8 w-0.5 bg-ink" />
                        <ArrowDown className="h-3 w-3" />
                    </motion.div>
                </div>
            </MacWindow>
        </div>
    );
}
